import React, { useMemo } from 'react';
import PropTypes from 'prop-types';
import { useTranslation } from 'react-i18next';
import Icon from '../../../../Icon/Icon';

const normalizeTypeValue = (value) => {
    if (value === null || value === undefined) return '';
    const normalized = String(value).trim();
    if (!normalized) return '';
    return normalized.replace(/\s+/g, '_').toUpperCase();
};

const iconForType = (value) => {
    switch (value) {
        case 'OUTDOOR':
            return <Icon className="hidden lg:inline" name="road" />;
        case 'INDOOR':
            return <Icon className="hidden lg:inline" name="door-open" />;
        default:
            return null;
    }
};

const ScreenTypeOption = ({ identifier, label, isChecked, onToggle }) => {
    const handleChange = (event) => {
        onToggle(event.target.checked);
    };

    return (
        <div className="flex items-center justify-between my-2">
            <div className="flex items-center">
                <input
                    checked={isChecked}
                    onChange={handleChange}
                    name={`screen-type-${identifier}`}
                    id={`screen-type-${identifier}`}
                    type="checkbox"
                    className="h-4 w-4 text-blue-600 focus:ring-0 border-gray-300 rounded"
                />
                <label
                    htmlFor={`screen-type-${identifier}`}
                    className="ml-2 block text-sm text-gray-600 flex items-center gap-2 items-center">
                    {iconForType(identifier)}
                    <span>{label}</span>
                </label>
            </div>
        </div>
    );
};

ScreenTypeOption.propTypes = {
    identifier: PropTypes.string.isRequired,
    label: PropTypes.string.isRequired,
    count: PropTypes.number,
    isChecked: PropTypes.bool.isRequired,
    onToggle: PropTypes.func.isRequired,
};

const translationKeyByValue = {
    OUTDOOR: 'sections.campaigns.new-campaign.wizard-screens.filters.screenLocation-outdoor',
    INDOOR: 'sections.campaigns.new-campaign.wizard-screens.filters.screenLocation-indoor',
};

const SCREEN_TYPE_VALUES = ['OUTDOOR', 'INDOOR'];

const ScreenTypeList = ({
    screens = [],
    screensFilteredFromScreensWizard = [],
    filteredScreensLocal = [],
    selectedTypes = [],
    onChange,
    selectedPublishers = [],
    isFilterByAudiences = false,
}) => {
    const { t } = useTranslation();

    const screensForCounts = useMemo(() => {
        if ((selectedTypes?.length ?? 0) === 0 && (selectedPublishers?.length ?? 0) === 0) {
            return isFilterByAudiences
                ? screensFilteredFromScreensWizard
                : screens;
        }

        const base = Array.isArray(filteredScreensLocal)
            ? filteredScreensLocal
            : [];

        if (!isFilterByAudiences) return base;

        return base.filter((screen) => screen?.audiences);
    }, [
        screens,
        screensFilteredFromScreensWizard,
        filteredScreensLocal,
        selectedTypes,
        selectedPublishers,
        isFilterByAudiences,
    ]);

    const counts = useMemo(() => {
        const counters = {
            OUTDOOR: 0,
            INDOOR: 0,
        };

        (screensForCounts || []).forEach((screen) => {
            const value = normalizeTypeValue(screen?.screenType);
            if (!value) return;
            if (value === 'OUTDOOR' || value === 'INDOOR') {
                counters[value] += 1;
            }
        });

        return counters;
    }, [screensForCounts]);

    const options = useMemo(
        () =>
            SCREEN_TYPE_VALUES.map((value) => {
                const translationKey = translationKeyByValue[value];
                const fallbackLabel = value
                    .split('_')
                    .map(
                        (chunk) =>
                            chunk.charAt(0) + chunk.slice(1).toLowerCase()
                    )
                    .join(' ');
                return {
                    value,
                    label: translationKey ? t(translationKey) : fallbackLabel,
                    count: counts[value] ?? 0,
                };
            }),
        [counts, t]
    );

    const selectedSet = useMemo(() => {
        const set = new Set();
        (selectedTypes || []).forEach((value) => {
            const normalized = normalizeTypeValue(value);
            if (normalized) set.add(normalized);
        });
        return set;
    }, [selectedTypes]);

    const handleToggle = (typeValue, checked) => {
        if (!onChange || !typeValue) return;
        let next = selectedTypes || [];

        if (checked) {
            if (selectedSet.has(typeValue)) return;
            next = [...next, typeValue];
        } else {
            next = next.filter(
                (value) => normalizeTypeValue(value) !== typeValue
            );
        }

        onChange(
            Array.from(
                new Set(next.map((value) => normalizeTypeValue(value)).filter(Boolean))
            )
        );
    };

    return (
        <div className="font-light py-2 w-full">
            <div className="title font-light">
                {t(
                    'sections.campaigns.new-campaign.wizard-screens.filters.screenLocation-label'
                )}
            </div>
            {options.map((option) => (
                <ScreenTypeOption
                    key={option.value}
                    identifier={option.value}
                    label={option.label}
                    isChecked={selectedSet.has(option.value)}
                    onToggle={(checked) => handleToggle(option.value, checked)}
                />
            ))}
        </div>
    );
};

ScreenTypeList.propTypes = {
    screens: PropTypes.array,
    screensFilteredFromScreensWizard: PropTypes.array,
    filteredScreensLocal: PropTypes.array,
    selectedTypes: PropTypes.array,
    onChange: PropTypes.func,
    selectedPublishers: PropTypes.array,
    isFilterByAudiences: PropTypes.bool,
};

export default ScreenTypeList;
